import {
  createContext,
  useState,
  useContext,
  useEffect,
  useCallback,
  useMemo,
  useRef,
} from 'react';
import { useSelector } from 'react-redux';
import { useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import qualityMetricApi from 'shared/api/quality-metric.service.api';
import { getLang } from 'shared/store/selectors/lang.selector';
import { isQualityMetricsCasesEnabled } from 'shared/store/selectors/features.selector';
import {
  EVENTS,
  INTERNAL_ROUTING,
  QUALITY_METRICS_NAVIGATION_SOURCE,
  ROUTES,
} from 'shared/constants';
import {
  adjustTrend,
  calculateGoalPercentage,
  calculateTrendDelta,
  deriveMetricsTrends,
  displayPercentage,
} from 'shared/utils/quality-metrics.utils';
import analyticsService from 'src/helpers/analytics.service';

const QualityMetricsContext = createContext();

export function useQualityMetricsContext() {
  return useContext(QualityMetricsContext);
}

const TAB_KEYS = {
  OVERVIEW: 'overview',
  CASES: 'cases',
  RESOURCES: 'resources',
  DEFINITION: 'definition',
};

export { TAB_KEYS };

const VALID_TABS = new Set(Object.values(TAB_KEYS));

export function QualityMetricsProvider({ children }) {
  const { metricId: metricIdParam } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const { state: locationState } = useLocation();

  const lang = useSelector(getLang('QUALITY_METRIC'));
  const showCasesTab = useSelector(isQualityMetricsCasesEnabled);

  const {
    source = QUALITY_METRICS_NAVIGATION_SOURCE.QUALITY_METRIC_WIDGET,
    notificationId = null,
  } = locationState || {};

  // List state
  const [metrics, setMetrics] = useState([]);
  const [loading, setLoading] = useState(true);

  // Selection state (metricId from path param, tab from query param)
  const selectedMetricId = metricIdParam || null;
  const tabFromUrl = searchParams.get(INTERNAL_ROUTING.QUERY_PARAMS.QUALITY_METRIC_TAB);
  const [selectedTab, setSelectedTabState] = useState(
    VALID_TABS.has(tabFromUrl) ? tabFromUrl : TAB_KEYS.OVERVIEW,
  );

  // Error state
  const [error, setError] = useState(null);
  const [errorDetail, setErrorDetail] = useState(null);

  // Detail state
  const [qualityMetric, setQualityMetric] = useState(null);
  const [userMetric, setUserMetric] = useState(null);
  const [loadingDetail, setLoadingDetail] = useState(false);

  // Sidebar trend data — derived synchronously from metrics (trends ship embedded on each item)
  const { userTrendsMap, rawUserTrendsMap, sortedMetrics, comparisonLabel } = useMemo(
    () => deriveMetricsTrends(metrics),
    [metrics],
  );

  // Fetch metrics list on mount
  useEffect(() => {
    const fetchMetrics = async () => {
      setLoading(true);
      setError(null);

      try {
        const { data, error: fetchError } = await qualityMetricApi.getMyMetrics();
        setLoading(false);

        if (fetchError) {
          console.error('Error fetching metrics:', fetchError);
          setError(fetchError);
          return;
        }

        if (data?.metrics) {
          setMetrics(data.metrics);
        }
      } catch (err) {
        console.error('Unexpected error fetching metrics:', err);
        setLoading(false);
        setError(err);
      }
    };

    fetchMetrics();
  }, []);

  // Auto-select first metric if none selected (redirect to path param URL)
  useEffect(() => {
    if (selectedMetricId) return;

    const displayMetrics = sortedMetrics.length ? sortedMetrics : metrics;
    const firstMetricId = displayMetrics[0]?.metricId;

    if (firstMetricId) {
      navigate(`${ROUTES.QUALITY_METRIC}/${firstMetricId}?tab=${TAB_KEYS.OVERVIEW}`, {
        replace: true,
        state: locationState,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [metrics, sortedMetrics, selectedMetricId]);

  // Per-id cache for detail responses — re-selecting a metric is instant after the first fetch.
  // Lives for the provider's lifetime; cleared on remount (page refresh / route exit).
  const detailCacheRef = useRef(new Map());

  // Fetch metric detail when selected metric changes
  useEffect(() => {
    if (!selectedMetricId) return;

    const trackOpenAnalytics = (qm, um) => {
      analyticsService.track(EVENTS.QUALITY_METRICS.OPEN, {
        source,
        notificationId,
        userId: um.userId,
        metricId: selectedMetricId,
        metricName: qm.title,
        goal: um.groupGoal,
        userCurrentNumerator: um.totalSuccess,
        userCurrentDenominator: um.totalRecords,
        userCurrentValue: displayPercentage({
          total: um.totalRecords,
          success: um.totalSuccess,
          invert: um.invert,
        }),
        groupCurrentNumerator: qm.totalGroupSuccess,
        groupCurrentDenominator: qm.totalGroupRecords,
        groupCurrentValue: displayPercentage({
          total: qm.totalGroupRecords,
          success: qm.totalGroupSuccess,
          invert: qm.invert,
        }),
        startDate: qm.startDate,
        importDate: um.updatedAt,
      });
    };

    // Cache hit — serve instantly, no skeleton flash, no network call.
    const cached = detailCacheRef.current.get(selectedMetricId);
    if (cached) {
      setQualityMetric(cached.qualityMetric);
      setUserMetric(cached.userMetric);
      setLoadingDetail(false);
      setErrorDetail(null);
      trackOpenAnalytics(cached.qualityMetric, cached.userMetric);
      return;
    }

    const controller = new AbortController();

    const fetchDetail = async () => {
      setLoadingDetail(true);
      setErrorDetail(null);
      setQualityMetric(null);
      setUserMetric(null);

      try {
        const result = await qualityMetricApi.getQualityMetric({
          id: selectedMetricId,
          withUserMetric: true,
          signal: controller.signal,
        });

        if (controller.signal.aborted) return;

        setLoadingDetail(false);

        if (result.error) {
          console.error('Error fetching metric detail:', result.error);
          setErrorDetail(result.error);
          return;
        }

        const qm = result.data?.qualityMetric || null;
        const um = result.data?.userMetric || null;

        setQualityMetric(qm);
        setUserMetric(um);

        if (qm && um) {
          detailCacheRef.current.set(selectedMetricId, { qualityMetric: qm, userMetric: um });
          trackOpenAnalytics(qm, um);
        }
      } catch (err) {
        if (controller.signal.aborted) return;
        console.error('Unexpected error fetching metric detail:', err);
        setLoadingDetail(false);
        setErrorDetail(err);
      }
    };

    fetchDetail();

    return () => {
      controller.abort();
    };
  }, [selectedMetricId, source, notificationId]);

  // User-side trend delta for the sidebar (computed across all metrics)
  const userTrendDelta = selectedMetricId ? userTrendsMap[selectedMetricId] || null : null;

  // Group-side trend delta comes from the detail fetch (qualityMetric.groupTrends),
  // since /user does not include the group series.
  const groupTrendDelta = useMemo(
    () =>
      qualityMetric?.groupTrends
        ? calculateTrendDelta({
            trends: qualityMetric.groupTrends,
            invert: qualityMetric.invert,
          })
        : null,
    [qualityMetric],
  );

  // Prefer the detail-fetch's userMetric.trends for the selected metric so the chart
  // is consistent with the rest of the detail panel; fall back to the sidebar's raw map.
  const rawUserTrends =
    userMetric?.trends ?? (selectedMetricId ? rawUserTrendsMap[selectedMetricId] : null);
  const rawGroupTrends = qualityMetric?.groupTrends ?? null;

  const processedUserTrends = useMemo(
    () =>
      rawUserTrends && qualityMetric
        ? rawUserTrends.filter(item => item.rate !== null).map(adjustTrend(qualityMetric.invert))
        : [],
    [rawUserTrends, qualityMetric],
  );

  const processedGroupTrends = useMemo(
    () =>
      rawGroupTrends && qualityMetric
        ? rawGroupTrends.filter(item => item.rate !== null).map(adjustTrend(qualityMetric.invert))
        : [],
    [rawGroupTrends, qualityMetric],
  );

  const goalPercentage = useMemo(
    () => (qualityMetric ? calculateGoalPercentage(qualityMetric) : null),
    [qualityMetric],
  );

  // Public setters — sync URL directly on user action
  const setSelectedMetricId = useCallback(
    id => {
      if (id === selectedMetricId) return;

      setSelectedTabState(TAB_KEYS.OVERVIEW);
      // Switching metrics in-page is a new navigation context — report the page as the source.
      navigate(`${ROUTES.QUALITY_METRIC}/${id}?tab=${TAB_KEYS.OVERVIEW}`, {
        replace: true,
        state: { source: QUALITY_METRICS_NAVIGATION_SOURCE.QUALITY_METRICS_PAGE },
      });
    },
    [navigate, selectedMetricId],
  );

  const setSelectedTab = useCallback(
    tab => {
      setSelectedTabState(tab);
      setSearchParams(
        prev => {
          prev.set(INTERNAL_ROUTING.QUERY_PARAMS.QUALITY_METRIC_TAB, tab);
          return prev;
        },
        { replace: true, state: locationState },
      );
      analyticsService.track(EVENTS.QUALITY_METRICS.TAB_CLICK, {
        tab,
        metricId: selectedMetricId,
      });
    },
    [selectedMetricId, setSearchParams, locationState],
  );

  const displayMetrics = sortedMetrics.length ? sortedMetrics : metrics;

  const value = {
    // List
    metrics: displayMetrics,
    loading,
    error,
    userTrendsMap,
    comparisonLabel,

    // Selection
    selectedMetricId,
    setSelectedMetricId,
    selectedTab,
    setSelectedTab,

    // Detail
    qualityMetric,
    userMetric,
    loadingDetail,
    errorDetail,
    userTrendDelta,
    groupTrendDelta,
    processedUserTrends,
    processedGroupTrends,
    goalPercentage,

    // Feature flags
    showCasesTab,

    // Lang
    lang,
  };

  return <QualityMetricsContext.Provider value={value}>{children}</QualityMetricsContext.Provider>;
}
